import PropTypes from 'prop-types';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useReducer
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSpacesApi } from 'src/api/space.api';
import { updateSelectedSpace } from 'src/api/workspace.api';
import { selectSpaceFromUser } from 'src/hooks/useSpace';
import { MemberStates } from 'src/models/enums/memberStates.enum';
import { User, UserWorkspace } from 'src/models/user.interface';
import { Workspace } from 'src/models/workspace.interface';
import { getAssetTypes } from 'src/slices/workspaceAssetType';
import { getWorkspaceLocations } from 'src/slices/workspaceLocation';
import {
  getOrganisationUserFeatures,
  getWorkspaceUserFeatures
} from 'src/slices/workspaceUser';
import { useDispatch } from 'src/store';
import { resetFilters, resetWorkspace } from 'src/store/extraActions';
import axiosCore from 'src/utils/axiosCore';
import {
  setUserToDatadogRUM,
  unsetUserDatadogRUM
} from 'src/utils/datadog.util';
import { isValidToken } from 'src/utils/jwt';
import { PosthogAnalytics } from 'src/utils/posthogAnalytics';

interface AuthState {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | null;
  allSpaces?: Array<
    Pick<Workspace, '_id' | 'name' | 'images' | 'description'> & {
      type: string;
    }
  >;
}

interface AuthContextValue extends AuthState {
  method: 'JWT';
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    workspaceId: string,
    role: string,
    organisationId: string
  ) => Promise<void>;
  recoverPassword: (email: string, origin: string) => Promise<void>;
  setNewPassword: (
    userId: string,
    password: string,
    passwordToken: string
  ) => Promise<void>;
  switchWorkspace: (_id: string) => Promise<void>;
  switchLocation: (workspaceId: string, locationId: string) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    allSpaces?: Array<
      Pick<Workspace, '_id' | 'name' | 'images' | 'description'> & {
        type: string;
      }
    >;
    workspace: UserWorkspace | null;
    user: User | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    allSpaces?: Array<
      Pick<Workspace, '_id' | 'name' | 'images' | 'description'> & {
        type: string;
      }
    >;
    user: User;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
  payload: {
    user: User;
  };
};

type RecoverPasswordAction = {
  type: 'RECOVERPASSWORD';
  payload: {
    message: string;
  };
};

type SetNewPasswordAction = {
  type: 'SETNEWPASSWORD';
  payload: {
    message: string;
  };
};

type SwitchWorkspaceAction = {
  type: 'SWITCHWORKSPACE';
  payload: {
    user: User;
  };
};

type SwitchLocationAction = {
  type: 'SWITCHLOCATION';
  payload: {
    user: User;
  };
};

type Action =
  | InitializeAction
  | LoginAction
  | LogoutAction
  | RegisterAction
  | RecoverPasswordAction
  | SetNewPasswordAction
  | SwitchWorkspaceAction
  | SwitchLocationAction;

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const setSession = (
  authToken: string | null,
  refreshToken: string | null,
  userId: string | null
): void => {
  if (authToken) {
    window.localStorage.setItem('authToken', authToken);
    window.localStorage.setItem('refreshToken', refreshToken);
    window.localStorage.setItem('userId', userId);
    axiosCore.defaults.headers.common.Authorization = `Bearer ${authToken}`;
    axiosCore.defaults.headers.common['refresh-token'] = refreshToken;
  } else {
    window.localStorage.removeItem('authToken');
    window.localStorage.removeItem('refreshToken');
    window.localStorage.removeItem('userId');
    delete axiosCore.defaults.headers.common.Authorization;
    delete axiosCore.defaults.headers.common['refresh-token'];
  }
};

const additionalUserProps = (user: User) => {
  return {
    name: `${user.firstName} ${user.lastName}`,
    avatar: user.images[0]?.imageUrl
  };
};

const handlers: Record<
  string,
  (state: AuthState, action: Action) => AuthState
> = {
  INITIALIZE: (state: AuthState, action: InitializeAction): AuthState => {
    const { isAuthenticated, user, allSpaces } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      allSpaces
    };
  },
  LOGIN: (state: AuthState, action: LoginAction): AuthState => {
    const { user, allSpaces } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      allSpaces
    };
  },
  LOGOUT: (state: AuthState): AuthState => ({
    ...state,
    isAuthenticated: false,
    user: null,
    allSpaces: []
  }),
  REGISTER: (state: AuthState, action: RegisterAction): AuthState => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  RECOVERPASSWORD: (state: AuthState): AuthState => {
    return {
      ...state,
      isAuthenticated: false,
      user: null
    };
  },
  SETNEWPASSWORD: (state: AuthState): AuthState => {
    return {
      ...state,
      isAuthenticated: false,
      user: null
    };
  },
  SWITCHWORKSPACE: (
    state: AuthState,
    action: SwitchWorkspaceAction
  ): AuthState => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  SWITCHLOCATION: (
    state: AuthState,
    action: SwitchLocationAction
  ): AuthState => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  }
};

const reducer = (state: AuthState, action: Action): AuthState =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  recoverPassword: () => Promise.resolve(),
  setNewPassword: () => Promise.resolve(),
  switchWorkspace: (_id: string) => Promise.resolve(),
  switchLocation: (_id: string) => Promise.resolve()
});

const setAnalyticsUser = (user: User) => {
  setUserToDatadogRUM(user);
  PosthogAnalytics.identifyUser(user);
};

const unsetAnalyticsUser = () => {
  unsetUserDatadogRUM();
};

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { t } = useTranslation();
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const reduxDispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const initializeUser = useCallback(
    (user: User) => {
      const space = selectSpaceFromUser(user);
      if (space.spaceType === 'workspace') {
        reduxDispatch(getWorkspaceUserFeatures(space._id));
        reduxDispatch(getWorkspaceLocations(space._id));
      } else {
        reduxDispatch(getOrganisationUserFeatures(space._id));
      }
      reduxDispatch(getAssetTypes(space._id, space.spaceType));
    },
    [reduxDispatch]
  );

  const switchWorkspace = useCallback(
    async (spaceId: string) => {
      const response = await updateSelectedSpace({ spaceId });
      const user = response.data;

      setAnalyticsUser(user);

      reduxDispatch(resetWorkspace());

      dispatch({
        type: 'SWITCHWORKSPACE',
        payload: {
          user: {
            ...user,
            name: `${user.firstName} ${user.lastName}`,
            avatar: user.images[0]?.imageUrl
          }
        }
      });

      initializeUser(user);

      reduxDispatch(resetFilters());

      navigate('/activity/list');
    },
    [reduxDispatch, initializeUser, navigate]
  );

  const switchLocation = useCallback(
    async (workspaceId: string, locationId: string) => {
      const response = await axiosCore.put<{
        message: string;
        data: User;
      }>(`/workspaces/${workspaceId}/user/location`, { location: locationId });
      const user = response.data.data;

      setAnalyticsUser(user);

      dispatch({
        type: 'SWITCHLOCATION',
        payload: {
          user: {
            ...user,
            name: `${user.firstName} ${user.lastName}`,
            avatar: user.images[0]?.imageUrl
          }
        }
      });

      reduxDispatch(resetFilters());
    },
    [reduxDispatch]
  );

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const authToken = window.localStorage.getItem('authToken');
        const refreshToken = window.localStorage.getItem('refreshToken');
        const userId = window.localStorage.getItem('userId');

        // The authToken is so short lived that it is not worthy of validating,so check the refresh token instead
        // or consider making the authToken live longer and get rid of the refresh token altogether
        if (authToken && refreshToken && isValidToken(refreshToken)) {
          setSession(authToken, refreshToken, userId);

          const userResponse = await axiosCore.get<{ data: User }>(`/user`);
          const user = userResponse.data.data;

          setAnalyticsUser(user);

          const selectedWorkspace = user.workspaces?.find(
            (w) =>
              w._id === user.selectedSpace &&
              w.member.state === MemberStates.APPROVED
          );

          let allSpaces = [];

          if (user.isSupportUser) {
            const { data } = await getSpacesApi();
            allSpaces = data;
          }

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              workspace: selectedWorkspace,
              allSpaces,
              user: {
                ...user,
                ...additionalUserProps(user)
              }
            }
          });

          initializeUser(user);
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              workspace: null,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            workspace: null,
            user: null
          }
        });
      }
    };

    initialize();
  }, [reduxDispatch, initializeUser]);

  const login = async (email: string, password: string): Promise<void> => {
    const response = await axiosCore.post<{
      data: {
        authToken: string;
        refreshToken: string;
        userId: string;
      };
    }>(`/auth/login`, {
      email,
      password
    });
    const { authToken, refreshToken, userId } = response.data.data;

    setSession(authToken, refreshToken, userId);

    const userResponse = await axiosCore.get<{ data: User }>(`/user`);
    const user = userResponse.data.data;

    setAnalyticsUser(user);

    reduxDispatch(resetWorkspace());

    let allSpaces = [];

    if (user.isSupportUser) {
      const { data } = await getSpacesApi();
      allSpaces = data;
    }

    dispatch({
      type: 'LOGIN',
      payload: {
        allSpaces,
        user: {
          ...user,
          ...additionalUserProps(user)
        }
      }
    });

    initializeUser(user);
  };

  const logout = async (): Promise<void> => {
    setSession(null, null, null);
    unsetAnalyticsUser();
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    workspaceId: string,
    role: string,
    organisationId: string
  ): Promise<void> => {
    const endpoint = workspaceId
      ? `/auth/signup/${workspaceId}`
      : `/auth/organisation/signup/${organisationId}`;

    const response = await axiosCore.post<{
      data: {
        authToken: string;
        refreshToken: string;
        userId: string;
      };
    }>(endpoint, {
      email,
      password,
      firstName,
      lastName,
      role
    });
    const { authToken, refreshToken, userId } = response.data.data;

    setSession(authToken, refreshToken, userId);

    const userResponse = await axiosCore.get<{ data: User }>(`/user`);
    const user = userResponse.data.data;

    const selectedWorkspace = user.workspaces?.find(
      (w) =>
        w._id === user.selectedSpace && w.member.state === MemberStates.APPROVED
    );

    if (!selectedWorkspace) {
      throw new Error(
        t(
          'Your account does not have an approved workspace set. Check with your workspace adminstrator.'
        )
      );
    }

    setAnalyticsUser(user);

    dispatch({
      type: 'REGISTER',
      payload: {
        user: {
          ...user,
          ...additionalUserProps(user)
        }
      }
    });

    initializeUser(user);
  };

  const recoverPassword = async (
    email: string,
    origin: string
  ): Promise<void> => {
    const response = await axiosCore.post<{
      data: {
        message: string;
        currentStyle: string;
        error: [];
      };
    }>(`/auth/reset`, {
      email,
      originUrl: `${origin}/account/set-new-password`
    });

    setSession(null, null, null);

    dispatch({
      type: 'RECOVERPASSWORD',
      payload: { message: response.data.data?.message }
    });
  };

  const setNewPassword = async (
    userId: string,
    password: string,
    passwordToken: string
  ): Promise<void> => {
    const response = await axiosCore.post<{
      data: {
        message: string;
      };
    }>(`/auth/new-password`, {
      userId,
      password,
      passwordToken
    });

    setSession(null, null, null);

    dispatch({
      type: 'SETNEWPASSWORD',
      payload: { message: response.data.data?.message }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        register,
        recoverPassword,
        setNewPassword,
        switchWorkspace,
        switchLocation
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
